import React from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  GiftOutlined,
  TeamOutlined,
  UserAddOutlined,
  StarOutlined,
} from "@ant-design/icons";
import SummaryCard, { SummaryItem } from "./SummaryCard";
import "./PeopleInsights.scss";

interface Person {
  firstName: string;
  lastName: string;
  birthday?: string;
  position?: string;
  contractStart?: string;
}

interface PeopleInsightsProps {
  birthdays?: Person[];
  leaves?: Person[];
  newUsers?: Person[];
  userAnniversaries?: Person[];
  loading?: boolean;
}

const getBirthdayLabel = (birthday: string): string => {
  const today = dayjs().startOf("day");
  const bdayThisYear = dayjs(birthday).year(today.year());
  const diff = bdayThisYear.diff(today, "day");
  if (diff === 0) return "Danas";
  if (diff > 0) return `Za ${diff}d`;
  return `Prije ${Math.abs(diff)}d`;
};

const getAnniversaryYears = (startDate: string): string => {
  const years = dayjs().diff(dayjs(startDate), "year");
  return `${years} god.`;
};

const PeopleInsights: React.FC<PeopleInsightsProps> = ({
  birthdays = [],
  leaves = [],
  newUsers = [],
  userAnniversaries = [],
  loading = false,
}) => {
  const { t } = useTranslation();

  const birthdayItems: SummaryItem[] = (birthdays ?? []).map((p) => ({
    firstName: p.firstName,
    lastName: p.lastName,
    secondary: p.birthday ? getBirthdayLabel(p.birthday) : undefined,
  }));

  const leavesItems: SummaryItem[] = (leaves ?? []).map((p) => ({
    firstName: p.firstName,
    lastName: p.lastName,
    secondary: p.position || t("On leave"),
  }));

  const newUsersItems: SummaryItem[] = (newUsers ?? []).map((p) => {
    let secondary = p.position || undefined;
    if (p.contractStart) {
      const days = dayjs().startOf("day").diff(dayjs(p.contractStart).startOf("day"), "day");
      const daysLabel = days === 0 ? "Danas" : days === 1 ? "Prije 1d" : `Prije ${days}d`;
      secondary = p.position ? `${p.position} · ${daysLabel}` : daysLabel;
    }
    return { firstName: p.firstName, lastName: p.lastName, secondary };
  });

  const anniversaryItems: SummaryItem[] = (userAnniversaries ?? []).map(
    (p) => ({
      firstName: p.firstName,
      lastName: p.lastName,
      secondary: p.birthday ? getAnniversaryYears(p.birthday) : undefined,
    }),
  );

  return (
    <div className="pi-grid">
      <SummaryCard
        icon={<GiftOutlined />}
        title={t("Upcoming Birthdays")}
        subtitle={t("Upcoming")}
        count={birthdays?.length ?? 0}
        accentColor="#d97706"
        accentBg="#fffbeb"
        avatarColor="#92400e"
        avatarBg="#fde68a"
        items={birthdayItems}
        loading={loading}
        emptyText={t("No upcoming birthdays")}
      />
<SummaryCard
        icon={<UserAddOutlined />}
        title={t("New Team Members")}
        subtitle={t("This month")}
        count={newUsers?.length ?? 0}
        accentColor="#059669"
        accentBg="#ecfdf5"
        avatarColor="#064e3b"
        avatarBg="#a7f3d0"
        items={newUsersItems}
        loading={loading}
        emptyText={t("No new members this month")}
      />
      <SummaryCard
        icon={<StarOutlined />}
        title={t("Work Anniversaries")}
        subtitle={t("This month")}
        count={userAnniversaries?.length ?? 0}
        accentColor="#7c3aed"
        accentBg="#f5f3ff"
        avatarColor="#4c1d95"
        avatarBg="#ddd6fe"
        items={anniversaryItems}
        loading={loading}
        emptyText={t("No anniversaries this month")}
      />
    </div>
  );
};

export default PeopleInsights;
